import React, { useState, useEffect } from 'react'
import { Card, Descriptions, Tag, Row, Col, Statistic, Spin } from 'antd'
import dayjs from 'dayjs'
import Agilite from 'agilite'
import BillingEnums from '../../../../utils/enums'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

const AccountDetailsContent = ({ accountData, formatCurrency }) => {
  const [patientData, setPatientData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPatientData = async () => {
      // Validate patientId before making API call
      const patientId = accountData?.patientId
      if (!patientId || typeof patientId !== 'string' || patientId.trim() === '' || patientId.length < 10) {
        console.warn('❌ AccountDetails - Invalid patientId, skipping patient data fetch:', {
          patientId: patientId,
          type: typeof patientId,
          length: patientId?.length
        })
        setLoading(false)
        setPatientData(null) // Explicitly set to null so we only show account data
        return
      }

      try {
        // Use projection to fetch only the fields we need for Account Details
        const response = await agilite.Connectors.execute('auth_users', BillingEnums.routeKeys.FIND_BY_ID, {
          recordId: accountData.patientId,
          projection: JSON.stringify({
            idNo: 1,
            dateOfBirth: 1,
            firstName: 1,
            lastName: 1,
            gender: 1,
            race: 1,
            medicalAid: 1, // Get the entire medicalAid object instead of individual fields
            billingId: 1,
            _id: 1
          })
        })

        setPatientData(response.data)
      } catch (error) {
        console.error('❌ Failed to fetch patient data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchPatientData()
  }, [accountData?.patientId])

  if (loading) {
    return <Spin size='large' />
  }

  // Combine account data and patient data
  const combinedData = {
    ...accountData,
    ...patientData,
    // Keep billing-specific fields from accountData
    billingId: accountData.billingId,
    outstandingAmount: accountData.outstandingAmount
  }

  // Helper to safely render values that might be objects
  const safeRender = (value, fallback = '-') => {
    if (value === null || value === undefined) return fallback
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  return (
    <div>
      <Card size='small' style={{ marginBottom: 16 }}>
        <Descriptions title='Account Information' bordered column={2}>
          <Descriptions.Item label='Account Number'>{combinedData.billingId}</Descriptions.Item>
          <Descriptions.Item label='Patient Name'>
            {combinedData.firstName && combinedData.lastName
              ? `${combinedData.firstName} ${combinedData.lastName}`
              : combinedData.patientName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label='Medical Aid'>
            {combinedData.medicalAid?.name ||
              combinedData.medicalAid?.schemeCode ||
              accountData.medicalAid ||
              'Private'}
          </Descriptions.Item>
          <Descriptions.Item label='Medical Aid Number'>
            {combinedData.medicalAid?.number || combinedData.medicalAid?.dependantNumber || '-'}
          </Descriptions.Item>
          <Descriptions.Item label='ID Number'>{combinedData.idNo || '-'}</Descriptions.Item>
          <Descriptions.Item label='Date of Birth'>
            {combinedData.dateOfBirth ? dayjs(combinedData.dateOfBirth).format('DD MMM YYYY') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label='Account Status'>
            <Tag color={combinedData.status === 'active' ? 'green' : 'red'}>
              {combinedData.status?.toUpperCase() || 'ACTIVE'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label='Gender'>{combinedData.gender || '-'}</Descriptions.Item>
          <Descriptions.Item label='Account Group'>{combinedData.accountGroup || 'General'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size='small' title='Billing Summary'>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Statistic
              title='Total Outstanding'
              value={combinedData.outstandingAmount || 0}
              precision={2}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: combinedData.outstandingAmount > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title='Medical Aid Due'
              value={combinedData.medicalAidOutstanding || 0}
              precision={2}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title='Patient Due'
              value={combinedData.patientOutstanding || 0}
              precision={2}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
        </Row>
      </Card>
    </div>
  )
}

export default AccountDetailsContent
